/**
 * EnterpriseReport — 企业跨班级报告 (R2) 渲染组件
 *
 * 6 sections (对齐 企业报告.html mockup §5.3)：
 *   1. 封面               cover (ENTERPRISE EDITION 徽章 + 企业名 + 班级数 + 人数 + 日期)
 *   2. 公司战略画像        scheduling.headline_tag + comprehensive_interpretation +
 *                          KPI + company L 分布 + 8 能力 + 3 素质 + 职位 (overview)
 *   3. 跨班级横向对比      overview.classes_kpi_matrix + cross_class_ability_heatmap (+overflow)
 *   4. 跨班级培训调度      scheduling.scheduling_recommendations + quarterly_schedule
 *   5. 各班级专题点评      scheduling.class_commentaries (class_id → 班级名)
 *   6. 封底
 *
 * Payload = build_enterprise_report_payload(): { scope, cover, overview, scheduling }.
 * Defensive: every field uses ?. + fallbacks — never throws on sparse payload.
 */
import React from 'react';
import './enterprise-report.css';

const PRIORITY_LABEL = { P0: '立即开展', P1: '本季跟进', P2: '后续观察' };
const L_GRADE_LABEL = { L1: 'L1 知晓尝试级', L2: 'L2 基础应用级', L3: 'L3 协同优化级', L4: 'L4 创新引领级' };

// 报告生成时间统一展示为北京时间（后端 generated_at 为 UTC ISO 串）。
// +8h 后读 UTC 各字段，结果与宿主机 / Playwright 容器时区无关。
function formatBeijingTime(iso) {
  if (!iso) return '';
  const s = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
  const bj = new Date(d.getTime() + 8 * 60 * 60 * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return (
    `${bj.getUTCFullYear()}-${p(bj.getUTCMonth() + 1)}-${p(bj.getUTCDate())} ` +
    `${p(bj.getUTCHours())}:${p(bj.getUTCMinutes())}:${p(bj.getUTCSeconds())}`
  );
}

function heatCssClass(score) {
  if (score == null) return 'er-hot-none';
  if (score >= 85) return 'er-hot-1';
  if (score >= 75) return 'er-hot-2';
  if (score >= 65) return 'er-hot-3';
  if (score >= 55) return 'er-hot-4';
  return 'er-hot-5';
}

function scoreToLGrade(score) {
  if (score == null) return null;
  if (score >= 85) return 'L4';
  if (score >= 70) return 'L3';
  if (score >= 55) return 'L2';
  return 'L1';
}

function scoreColor(score) {
  if (score == null) return '#64748b';
  if (score >= 75) return '#15803d';
  if (score >= 65) return '#1d4ed8';
  if (score >= 55) return '#d97706';
  return '#dc2626';
}

// ── Section 1: Cover ─────────────────────────────────────────────────────────

function CoverSection({ cover, overview }) {
  const enterpriseName = cover?.enterprise_name || overview?.enterprise?.name || '';
  const classCount = cover?.class_count ?? overview?.class_count ?? 0;
  const headcount = cover?.headcount ?? overview?.total_unique_headcount ?? 0;
  const industry = overview?.enterprise?.industry || '未指定';
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="er-page er-cover">
      <div className="er-brand-row">
        <div className="er-brand">QIANSHAO · AIQ ASSESSMENT</div>
        <div className="er-badge">★ ENTERPRISE EDITION ★</div>
      </div>
      <h1>
        前哨AI胜任力测评报告
        <br />
        <span style={{ color: 'rgba(255,255,255,0.7)' }}>·企业版</span>
      </h1>
      <div className="er-ent-name">{enterpriseName || '—'}</div>
      <div className="er-meta-grid">
        <div className="er-meta-cell">
          <div className="er-meta-label">所属行业</div>
          <div className="er-meta-value" style={{ fontSize: 16 }}>{industry}</div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">覆盖班级</div>
          <div className="er-meta-value">{classCount}<small>个班</small></div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">参与人数</div>
          <div className="er-meta-value">{headcount}<small>人</small></div>
        </div>
        <div className="er-meta-cell">
          <div className="er-meta-label">报告生成</div>
          <div className="er-meta-value" style={{ fontSize: 16 }}>{today}</div>
        </div>
      </div>
    </div>
  );
}

// ── Section 2: 公司战略画像 ──────────────────────────────────────────────────

function StrategyPortraitSection({ overview, scheduling }) {
  const headline = scheduling?.headline_tag || '';
  const interp = scheduling?.comprehensive_interpretation || '';
  const layer = overview?.company_layer_distribution || [];
  const radar = overview?.company_ability_radar || [];
  const traits = overview?.company_trait_distribution || [];
  const positions = overview?.company_position_top5 || [];

  const headcount = overview?.total_unique_headcount ?? 0;
  const completed = overview?.completed_headcount ?? 0;
  const completionRate = headcount > 0 ? ((completed / headcount) * 100).toFixed(1) : '0.0';
  const l4 = layer.find((l) => l.l_grade === 'L4');

  const totalLayered = layer.reduce((s, l) => s + (l.count || 0), 0);

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 01 · 公司战略画像</div>
      <h2 className="er-h2">全员综合画像</h2>

      {(headline || interp) && (
        <div className="er-headline-card">
          {headline && <div className="er-headline-tag">{headline}</div>}
          {interp && <div className="er-headline-summary">{interp}</div>}
        </div>
      )}

      <div className="er-kpi-grid">
        <div className="er-kpi">
          <div className="er-kpi-label">总参与人数</div>
          <div className="er-kpi-value" style={{ color: '#0f766e' }}>{headcount}<span className="er-kpi-unit">人</span></div>
          <div className="er-kpi-trend">{overview?.class_count ?? 0} 个班级 · 去重统计</div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">已完成率</div>
          <div className="er-kpi-value" style={{ color: '#15803d' }}>{completionRate}<span className="er-kpi-unit">%</span></div>
          <div className="er-kpi-trend">已完成 {completed} / {headcount}</div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">已完成人数</div>
          <div className="er-kpi-value" style={{ color: '#1a3a6b' }}>{completed}<span className="er-kpi-unit">人</span></div>
          <div className="er-kpi-trend">纳入全员聚合</div>
        </div>
        <div className="er-kpi">
          <div className="er-kpi-label">L4 创新引领</div>
          <div className="er-kpi-value" style={{ color: '#d4860b' }}>{l4?.count ?? 0}<span className="er-kpi-unit">人</span></div>
          <div className="er-kpi-trend">{l4 ? `占已完成 ${l4.pct}%` : '—'}</div>
        </div>
      </div>

      {layer.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">全员 L1-L4 等级分布</h3>
          <div className="er-l-stack">
            {layer.map((l) => {
              const pct = totalLayered > 0 ? (l.count / totalLayered) * 100 : 0;
              const cls = `er-l${(l.l_grade || 'L1').toLowerCase().replace('l', '')}`;
              return (
                <div key={l.l_grade} className={cls} style={{ width: `${pct}%`, minWidth: l.count > 0 ? 40 : 0 }}>
                  {l.count > 0 && `${l.l_grade} · ${l.count}`}
                </div>
              );
            })}
          </div>
          <div className="er-l-legend">
            {layer.map((l) => (
              <div key={l.l_grade} className="er-l-legend-item">
                <div className={`er-l-dot er-l${(l.l_grade || 'L1').toLowerCase().replace('l', '')}`} />
                {L_GRADE_LABEL[l.l_grade] || l.l_grade} · {l.count} 人 · {l.pct}%
              </div>
            ))}
          </div>
        </div>
      )}

      {radar.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">全员 8 能力均分</h3>
          {radar.map((a) => {
            const lg = scoreToLGrade(a.avg);
            const weak = a.avg != null && a.avg < 65;
            return (
              <div key={a.code} className={`er-score-row${weak ? ' weak' : ''}`}>
                <span>{a.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <b style={{ marginRight: 8, color: scoreColor(a.avg) }}>{a.avg ?? '—'}</b>
                  {lg && <span className={`er-lp er-lp-${lg}`}>{lg}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="er-row">
        {traits.length > 0 && (
          <div className="er-col er-card">
            <h3 className="er-h3">3 素质分布</h3>
            <div className="er-muted" style={{ marginBottom: 12, fontSize: 12 }}>全员均分（满分 4）</div>
            {traits.map((t) => (
              <div key={t.trait_code} className="er-trait-row">
                <div style={{ fontSize: 13 }}>{t.trait_name}</div>
                <div className="er-trait-bar">
                  {t.avg != null && <div style={{ width: `${(t.avg / 4) * 100}%` }} />}
                </div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{t.avg != null ? t.avg.toFixed(2) : '—'}</div>
              </div>
            ))}
          </div>
        )}
        {positions.length > 0 && (
          <div className="er-col er-card">
            <h3 className="er-h3">学员职位分布 · Top 5</h3>
            {positions.map((p) => (
              <div key={p.position} className="er-pos-row">
                <span>{p.position}</span>
                <b>{p.count} 人</b>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section 3: 跨班级横向对比 ────────────────────────────────────────────────

function CrossClassSection({ overview }) {
  const matrix = overview?.classes_kpi_matrix || [];
  const heatmap = overview?.cross_class_ability_heatmap || [];
  const overflow = overview?.classes_overflow;
  // ability column headers from the first heatmap row
  const abilityCols = (heatmap[0]?.abilities || []).map((a) => a.name);

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 02 · 跨班级横向对比</div>
      <h2 className="er-h2">各班级整体表现对比</h2>

      <div className="er-card">
        <div className="er-muted" style={{ marginBottom: 14, fontSize: 12 }}>按团队均分降序排列</div>
        {matrix.length === 0 ? (
          <div className="er-muted">暂无在训班级</div>
        ) : (
          <table className="er-matrix">
            <thead>
              <tr>
                <th>班级</th>
                <th>完成率</th>
                <th style={{ textAlign: 'right' }}>团队均分</th>
                <th>主要短板</th>
                <th style={{ textAlign: 'right' }}>风险条目</th>
                <th style={{ textAlign: 'right' }}>作答存疑率</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((c) => {
                const wfr = c.water_fill_rate;
                const wfrHigh = wfr != null && wfr > 30;
                return (
                  <tr key={c.class_id}>
                    <td>
                      <div className="er-class-name">{c.name}</div>
                      <div className="er-class-size">{c.size} 人</div>
                    </td>
                    <td className="er-muted">{c.completion_rate}%</td>
                    <td style={{ textAlign: 'right' }}>
                      <b style={{ color: scoreColor(c.team_avg) }}>{c.team_avg ?? '—'}</b>
                    </td>
                    <td>
                      {(c.short_dims || []).length > 0
                        ? (c.short_dims || []).map((d) => <span key={d.dim_code} className="er-short-chip">{d.dim_name}</span>)
                        : <span className="er-muted">—</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{c.risk_count ?? 0}</td>
                    <td style={{ textAlign: 'right', color: wfrHigh ? '#b45309' : undefined, fontWeight: wfrHigh ? 700 : undefined }}>
                      {wfr == null ? '—' : `${wfr}%`}
                    </td>
                  </tr>
                );
              })}
              {overflow && (
                <tr className="er-overflow-row">
                  <td colSpan={6}>其余 {overflow.count} 个班级 · 团队均分 {overflow.team_avg ?? '—'}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {heatmap.length > 0 && abilityCols.length > 0 && (
        <div className="er-card">
          <h3 className="er-h3">各班级 8 能力分项明细</h3>
          <div className="er-muted" style={{ marginBottom: 12, fontSize: 12 }}>每格 = 该班级在该维度的团队均分</div>
          <div className="er-heatmap-wrap">
            <table className="er-heatmap">
              <thead>
                <tr>
                  <th className="first">班级</th>
                  {abilityCols.map((name, i) => <th key={i}>{name}</th>)}
                </tr>
              </thead>
              <tbody>
                {heatmap.map((row) => (
                  <tr key={row.class_id}>
                    <td className="label">{row.name}<span className="sub">{(matrix.find((m) => m.class_id === row.class_id) || {}).size ?? ''} 人</span></td>
                    {(row.abilities || []).map((a, i) => (
                      <td key={i}>
                        <span className={`er-h-cell ${heatCssClass(a.avg)}`}>{a.avg ?? '—'}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section 4: 跨班级培训调度 ────────────────────────────────────────────────

function SchedulingSection({ scheduling, overview }) {
  const recs = scheduling?.scheduling_recommendations || [];
  const quarterly = scheduling?.quarterly_schedule || [];
  const matrix = overview?.classes_kpi_matrix || [];
  const idToName = {};
  matrix.forEach((c) => { idToName[c.class_id] = c.name; });
  const radar = overview?.company_ability_radar || [];
  const codeToName = {};
  radar.forEach((a) => { codeToName[a.code] = a.name; });

  if (recs.length === 0 && quarterly.length === 0) return null;

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 03 · 培训提升建议</div>
      <h2 className="er-h2">本期培训提升建议</h2>
      <p className="er-muted" style={{ marginBottom: 18, fontSize: 13 }}>
        下面按"先抓全员、再抓重点班"，列出本期要补什么、为什么、具体怎么做。该模块仅企业版报告出现。
      </p>

      {recs.map((r, idx) => (
        <div key={idx} className={`er-sched-card ${r.priority || 'P1'}`}>
          <div className="er-sched-header">
            <span className={`er-priority-pill ${r.priority || 'P1'}`}>{PRIORITY_LABEL[r.priority] || r.priority || ''}</span>
            <h3 className="er-sched-title">{r.title}</h3>
            {r.merge_strategy && <span className="er-merge-tag">{r.merge_strategy}</span>}
          </div>
          {(r.covered_dims || []).length > 0 && (
            <div className="er-muted" style={{ fontSize: 13, marginBottom: 6 }}>
              涉及能力 · {(r.covered_dims || []).map((c) => codeToName[c] || c).join('、')}
            </div>
          )}
          {(r.target_classes || []).length > 0 && (
            <div style={{ marginBottom: 4 }}>
              {(r.target_classes || []).map((cid) => (
                <span key={cid} className="er-chip">{idToName[cid] || `班级 ${cid}`}</span>
              ))}
            </div>
          )}
          {r.rationale_for_strategy && (
            <div className="er-sched-section">
              <div className="er-sched-section-label">为什么先补这块</div>
              <div className="er-sched-body">{r.rationale_for_strategy}</div>
            </div>
          )}
          {(r.improvement_directions || []).length > 0 && (
            <div className="er-sched-section">
              <div className="er-sched-section-label">具体怎么做</div>
              <ol className="er-sched-howto" style={{ margin: '6px 0 0', paddingLeft: 20, lineHeight: 1.75 }}>
                {(r.improvement_directions || []).map((d, i) => (
                  <li key={i} style={{ marginBottom: 5 }}>{d}</li>
                ))}
              </ol>
            </div>
          )}
          {r.resource_efficiency_note && (
            <div className="er-sched-section">
              <div className="er-sched-section-label">覆盖与节奏</div>
              <div className="er-sched-body">{r.resource_efficiency_note}</div>
            </div>
          )}
          <div className="er-sched-nums">
            <div><b>{r.predicted_coverage ?? 0} 人</b>预计覆盖</div>
            <div><b>{(r.target_classes || []).length} 班</b>{r.merge_strategy || ''}</div>
          </div>
        </div>
      ))}

      {quarterly.length > 0 && (
        <div className="er-card" style={{ background: '#f8fafc', marginTop: 24 }}>
          <h3 className="er-h3">本期培训资源 · 节奏建议</h3>
          <table className="er-q-table">
            <thead>
              <tr>
                <th>月份</th>
                <th>建议执行</th>
                <th style={{ textAlign: 'right' }}>覆盖</th>
              </tr>
            </thead>
            <tbody>
              {quarterly.map((q, i) => (
                <tr key={i}>
                  <td><b>{q.month_label}</b></td>
                  <td>{q.action}</td>
                  <td style={{ textAlign: 'right' }}><b>{q.coverage ? `${q.coverage} 人` : '—'}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Section 5: 各班级专题点评 ────────────────────────────────────────────────

function CommentariesSection({ scheduling, overview }) {
  const commentaries = scheduling?.class_commentaries || [];
  const matrix = overview?.classes_kpi_matrix || [];
  const idToKpi = {};
  matrix.forEach((c) => { idToKpi[c.class_id] = c; });

  if (commentaries.length === 0) return null;

  return (
    <div className="er-page">
      <div className="er-page-mark">PART 04 · 各班级专题点评</div>
      <h2 className="er-h2">各班级专题点评</h2>
      <p className="er-muted" style={{ marginBottom: 18, fontSize: 13 }}>
        从企业视角对每个班级单独点评，便于整体把握。各班详细数据请下载对应班级报告。
      </p>
      {commentaries.map((c) => {
        const kpi = idToKpi[c.class_id] || {};
        return (
          <div key={c.class_id} className="er-commentary">
            <div className="er-commentary-head">
              <h3 className="er-commentary-name">{kpi.name || `班级 ${c.class_id}`}</h3>
              <div className="er-commentary-meta">
                {kpi.size != null ? `${kpi.size} 人` : ''}
                {kpi.team_avg != null ? ` · 团队均分 ${kpi.team_avg}` : ''}
              </div>
            </div>
            <div className="er-commentary-text">{c.text}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Section 6: 封底 ──────────────────────────────────────────────────────────

function FooterSection({ scheduling, cover }) {
  const aiMeta = scheduling?.ai_meta || {};
  const generatedAt = scheduling?.generated_at || new Date().toISOString();
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="er-page er-footer-page">
      <div className="er-seal">END OF REPORT</div>
      <div className="er-footer-divider" />
      <p style={{ margin: '0 0 8px' }}><b style={{ color: '#0f172a' }}>关于本报告</b></p>
      <p style={{ margin: '0 0 8px' }}>
        本报告由前哨 AI 胜任力测评系统基于各班级已完成测评学员的数据跨班级聚合生成；班级矩阵 / 全员分布为系统聚合，调度建议部分由 AI 基于跨班级共性短板生成。
      </p>
      <p style={{ margin: '0 0 8px' }}>
        报告版本：<b>v5 · 企业版</b>
        {aiMeta.prompt_version != null && ` · prompt v${aiMeta.prompt_version}`}
        {' · '}报告生成时间：<b>{formatBeijingTime(generatedAt)}</b>
      </p>
      {cover?.enterprise_name && (
        <p style={{ margin: '0 0 8px' }}>企业：<b>{cover.enterprise_name}</b></p>
      )}
      <div className="er-footer-divider" />
      <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>
        © {today.slice(0, 4)} 前哨科技有限公司 · 本报告含跨班级聚合数据，请按企业合规策略保管
      </p>
    </div>
  );
}

// ── Root export ──────────────────────────────────────────────────────────────

// 风险维度群像（enterprise_class）：每维度一段众数群像文案，去档位 / 去颜色。
function EntRisksSection({ risks }) {
  const dims = risks?.dimensions || [];
  if (dims.length === 0) return null;
  return (
    <div className="er-page">
      <div className="er-page-mark">PART 03 · 风险维度群像</div>
      <h2 className="er-h2">风险维度群像</h2>
      {dims.map((d) => (
        <div key={d.dim_code} className="er-risk-dim">
          <div className="er-risk-dim__hd">
            {d.dim_name}　<span className="er-muted">{d.affected ?? 0}/{d.total ?? 0} 人存在短板</span>
          </div>
          <div className="er-risk-dim__bd">{d.enterprise_text}</div>
        </div>
      ))}
    </div>
  );
}

export default function EnterpriseReport({ payload, printMode }) {
  if (!payload) return null;
  const { cover, overview, scheduling, risks } = payload;
  return (
    <div className={`er-root${printMode ? ' print-mode' : ''}`}>
      <CoverSection cover={cover} overview={overview} />
      <StrategyPortraitSection overview={overview} scheduling={scheduling} />
      <CrossClassSection overview={overview} />
      <EntRisksSection risks={risks} />
      <SchedulingSection scheduling={scheduling} overview={overview} />
      <CommentariesSection scheduling={scheduling} overview={overview} />
      <FooterSection scheduling={scheduling} cover={cover} />
    </div>
  );
}
